import { z } from "zod";
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils.js";
/** Hash the unchanged response text identically on the server and in clients. */
export function runAnswerEvidenceTextSha256(text) {
    return bytesToHex(sha256(utf8ToBytes(text)));
}
const digest = z.string().regex(/^[a-f0-9]{64}$/u);
const identifier = z.string().trim().min(1).max(200);
export const runAnswerEvidenceReferenceSchema = z.object({
    certificateId: identifier,
    certificateSha256: digest,
    canonicalResultSha256: digest,
    rowIndex: z.number().int().nonnegative().max(9_999),
    column: z.string().min(1).max(240),
    jsonPointer: z.string().max(2_048).refine((value) => (value === "" || value.startsWith("/")) && !/~(?:[^01]|$)/u.test(value), "Invalid JSON pointer.").optional(),
}).strict();
/** Offsets are UTF-16, end-exclusive, into the unchanged Markdown label source. */
const answerEvidenceSchema = z.object({
    runId: identifier,
    attemptNumber: z.number().int().positive(),
    answerSha256: digest,
    scope: z.literal("referenced_spans_only"),
    spans: z.array(z.object({
        text: z.string().min(1).max(8_000),
        start: z.number().int().nonnegative().max(1_000_000),
        end: z.number().int().positive().max(1_000_000),
        references: z.array(runAnswerEvidenceReferenceSchema).min(1).max(16),
    }).strict().refine((span) => span.end - span.start === span.text.length, "Span offsets must match its exact source text length.")).max(256),
}).strict();
function orderedSpans(evidence, context) {
    for (let index = 1; index < evidence.spans.length; index += 1) {
        if (evidence.spans[index].start < evidence.spans[index - 1].end) {
            context.addIssue({ code: "custom", path: ["spans", index], message: "Source spans must be ordered and nonoverlapping." });
        }
    }
}
const answerEvidenceV1Schema = answerEvidenceSchema.extend({
    schemaVersion: z.literal("mlx.run-answer-evidence.v1"),
    status: z.enum(["source_references_verified", "uncited"]),
}).superRefine((evidence, context) => {
    orderedSpans(evidence, context);
    if ((evidence.spans.length === 0) !== (evidence.status === "uncited")) {
        context.addIssue({ code: "custom", path: ["status"], message: "Citation status must match the referenced spans." });
    }
});
/** Invalid model references are diagnostics, never certified source spans. */
export const runAnswerEvidenceIssueSchema = z.object({
    start: z.number().int().nonnegative(),
    end: z.number().int().nonnegative(),
    reason: z.string().regex(/^[a-z_]+$/u).max(80),
}).strict().refine((issue) => issue.end >= issue.start, "Invalid diagnostic offsets.");
export const currentRunAnswerEvidenceEventPayloadSchema = answerEvidenceSchema
    .extend({
    schemaVersion: z.literal("mlx.run-answer-evidence.v2"),
    status: z.enum(["source_references_verified", "uncited", "invalid_references"]),
    invalidReferences: z.array(runAnswerEvidenceIssueSchema).max(256),
}).superRefine((evidence, context) => {
    orderedSpans(evidence, context);
    const expected = evidence.invalidReferences.length ? "invalid_references"
        : evidence.spans.length ? "source_references_verified" : "uncited";
    if (evidence.status !== expected) {
        context.addIssue({ code: "custom", path: ["status"], message: "Citation status must match verified spans and invalid references." });
    }
});
/** Archived receipts remain evidence-only; every new writer emits v2. */
export const runAnswerEvidenceEventPayloadSchema = z.union([
    currentRunAnswerEvidenceEventPayloadSchema, answerEvidenceV1Schema,
]);
